import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import HeaderDashboard from '../header/headerDashboard';
import { EditBedForm } from '../forms/beds_edit';
import { markAsClean, fetchCleaningLogs } from '../../actions/cleaning';
import { fetchRentalHistory } from '../../actions/beds';
import { addMaintenanceLog, fetchMaintenanceLogs } from '../../actions/maintenance';
import { useSelector } from 'react-redux';

const STATUS_LABELS = {
    DISPONIBLE:  { label: 'Disponible',  color: '#22c55e', bg: 'rgba(34,197,94,0.2)' },
    EN_LOCATION: { label: 'En location', color: '#60a5fa', bg: 'rgba(96,165,250,0.2)' },
    SALE:        { label: 'Sale',        color: '#f87171', bg: 'rgba(248,113,113,0.2)' },
};

const BedStatus = ({ status }) => {
    const cfg = STATUS_LABELS[status] || STATUS_LABELS.DISPONIBLE;
    return (
        <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: 20,
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1.5px solid ${cfg.color}60`,
            fontWeight: '700',
            fontSize: 12,
            letterSpacing: '0.3px',
        }}>
            {status === 'SALE' && '⚠ '}
            {cfg.label}
        </span>
    );
};

const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
});

const CleaningHistory = ({ logs }) => {
    if (!logs.length) {
        return <p style={{ color: '#888', fontStyle: 'italic' }}>Aucun nettoyage enregistré.</p>;
    }
    return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
            {logs.map(log => (
                <li key={log._id} style={{
                    borderLeft: '3px solid #27ae60',
                    padding: '6px 12px',
                    marginBottom: 8,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4,
                }}>
                    <strong>{formatDate(log.cleaningDate)}</strong>
                    {' — '}
                    <span style={{ color: '#555' }}>Nettoyage / désinfection</span>
                    {log.declaredBy && (
                        <span style={{ color: '#aaa', fontSize: 12, marginLeft: 8 }}>
                            par {log.declaredBy}
                        </span>
                    )}
                </li>
            ))}
        </ul>
    );
};

const RentalHistory = ({ rentals }) => {
    if (!rentals.length) {
        return <p style={{ color: '#888', fontStyle: 'italic' }}>Aucune location enregistrée.</p>;
    }
    return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
            {rentals.map((rental, i) => (
                <li key={i} style={{
                    borderLeft: '3px solid #2980b9',
                    padding: '10px 14px',
                    marginBottom: 8,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4,
                }}>
                    <div style={{ fontWeight: 'bold', fontSize: 13 }}>
                        {rental.client || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Client inconnu</span>}
                        {rental.pharmacy && <span style={{ color: '#888', fontWeight: 'normal' }}> · {rental.pharmacy}</span>}
                        {rental.mattress && (
                            <span style={{ color: '#2980b9', fontWeight: 'normal', fontSize: 12 }}>
                                {' '}· 🛏️ matelas classe {rental.mattressClass || 2}
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                        Du {rental.startDate ? formatDate(rental.startDate) : '?'}{' '}
                        au {rental.endDate
                            ? formatDate(rental.endDate)
                            : <span style={{ color: '#27ae60', fontWeight: 'bold' }}>en cours</span>}
                    </div>
                </li>
            ))}
        </ul>
    );
};

const Badge = ({ label, color, bg }) => (
    <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        backgroundColor: bg || color + '18', color, border: `1px solid ${color}`,
        fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap',
    }}>{label}</span>
);

const MaintenanceHistory = ({ logs, bedClient }) => {
    if (!logs.length) {
        return <p style={{ color: '#888', fontStyle: 'italic' }}>Aucune maintenance enregistrée.</p>;
    }

    const typeColor  = t => t === 'PREVENTIVE' ? '#2980b9' : '#e67e22';
    const typeLabel  = t => t === 'PREVENTIVE' ? 'Préventive' : 'Corrective';
    const lieuLabel  = l => l === 'DOMICILE' ? 'Domicile' : 'Entrepôt';
    const lieuColor  = l => l === 'DOMICILE' ? '#c0392b' : '#7d3c98';

    return (
        <ul style={{ listStyle: 'none', padding: 0 }}>
            {logs.map(log => (
                <li key={log._id} style={{
                    borderLeft: `3px solid ${typeColor(log.type)}`,
                    padding: '10px 14px',
                    marginBottom: 10,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 4,
                }}>
                    {/* Ligne 1 : date + badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <strong style={{ fontSize: 13 }}>{formatDate(log.maintenanceDate)}</strong>
                        <Badge label={typeLabel(log.type)} color={typeColor(log.type)} />
                        {log.lieu && <Badge label={lieuLabel(log.lieu)} color={lieuColor(log.lieu)} />}
                        {log.resultat && (
                            <Badge
                                label={log.resultat === 'CONFORME' ? '✓ Conforme' : '✗ Non conforme'}
                                color={log.resultat === 'CONFORME' ? '#27ae60' : '#c0392b'}
                            />
                        )}
                    </div>
                    {/* Ligne 2 : technicien + déclarant */}
                    <div style={{ fontSize: 12, color: '#555', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {log.technicienNom && (
                            <span>🔧 <strong>Technicien :</strong> {log.technicienNom}</span>
                        )}
                        {log.declaredBy && log.declaredBy !== log.technicienNom && (
                            <span style={{ color: '#aaa' }}>Déclaré par : {log.declaredBy}</span>
                        )}
                    </div>
                    {/* Ligne 3 : signature domicile */}
                    {log.lieu === 'DOMICILE' && (
                        <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                                ✍️ <strong>Signataire :</strong>{' '}
                                {log.nomSignataire || (bedClient ? `${bedClient} (patient du lit)` : 'Patient du lit')}
                            </div>
                            {log.signaturePatient && (
                                <img
                                    src={log.signaturePatient}
                                    alt="Signature du patient"
                                    style={{
                                        display: 'block',
                                        maxWidth: 280,
                                        border: '1px solid #ddd',
                                        borderRadius: 4,
                                        backgroundColor: '#fafafa',
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {/* Ligne 4 : commentaire */}
                    {log.commentaire && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                            💬 {log.commentaire}
                        </div>
                    )}
                    {/* Rétrocompatibilité anciens logs */}
                    {!log.technicienNom && log.description && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{log.description}</div>
                    )}
                </li>
            ))}
        </ul>
    );
};

const SignatureModal = ({ onConfirm, onCancel }) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [hasSignature, setHasSignature] = useState(false);

    // Cale les dimensions internes du canvas sur son rendu réel (responsive)
    useEffect(() => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
    }, []);

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const src = e.touches ? e.touches[0] : e;
        return {
            x: (src.clientX - rect.left) * (canvas.width / rect.width),
            y: (src.clientY - rect.top)  * (canvas.height / rect.height),
        };
    };

    const startDraw = useCallback((e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        const { x, y } = getPos(e, canvas);
        canvas.getContext('2d').beginPath();
        canvas.getContext('2d').moveTo(x, y);
        drawing.current = true;
    }, []);

    const draw = useCallback((e) => {
        e.preventDefault();
        if (!drawing.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { x, y } = getPos(e, canvas);
        ctx.lineTo(x, y);
        ctx.strokeStyle = '#1a1a2e';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        setHasSignature(true);
    }, []);

    const stopDraw = useCallback(() => { drawing.current = false; }, []);

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onCancel}>×</button>

                <div style={{ padding: '20px 16px 16px' }}>
                    <h5 style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 15 }}>Signature du patient</h5>
                    <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                        Remettez l'écran au patient pour qu'il signe ci-dessous afin de valider l'intervention à domicile.
                    </p>

                    <div style={{
                        border: '2px dashed #ccc',
                        borderRadius: 8,
                        backgroundColor: '#fafafa',
                        position: 'relative',
                        marginBottom: 6,
                        touchAction: 'none',
                    }}>
                        {!hasSignature && (
                            <div style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#ccc', fontSize: 13, pointerEvents: 'none',
                            }}>
                                ✍️ Signez ici
                            </div>
                        )}
                        <canvas
                            ref={canvasRef}
                            style={{ display: 'block', width: '100%', height: 130, cursor: 'crosshair', borderRadius: 6 }}
                            onMouseDown={startDraw}
                            onMouseMove={draw}
                            onMouseUp={stopDraw}
                            onMouseLeave={stopDraw}
                            onTouchStart={startDraw}
                            onTouchMove={draw}
                            onTouchEnd={stopDraw}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                        <button type="button" className="btn btn-link btn-sm"
                            style={{ color: '#bbb', padding: 0, fontSize: 12 }} onClick={clearCanvas}>
                            Effacer
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn btn-success btn-sm" style={{ flex: 1 }}
                            disabled={!hasSignature}
                            onClick={() => onConfirm(canvasRef.current.toDataURL('image/png'))}>
                            ✓ Valider et enregistrer
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
                            Annuler
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const toDatetimeLocal = (d) => {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RadioGroup = ({ name, options, value, onChange }) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map(opt => (
            <label key={opt.value} style={{
                cursor: 'pointer',
                padding: '5px 14px',
                borderRadius: 20,
                border: `2px solid ${value === opt.value ? opt.color : '#ddd'}`,
                backgroundColor: value === opt.value ? opt.color + '18' : '#fff',
                fontWeight: value === opt.value ? 'bold' : 'normal',
                fontSize: 13,
                color: value === opt.value ? opt.color : '#555',
            }}>
                <input type="radio" name={name} value={opt.value} checked={value === opt.value}
                    onChange={() => onChange(opt.value)} style={{ display: 'none' }} />
                {opt.label}
            </label>
        ))}
    </div>
);

const FieldLabel = ({ children, required }) => (
    <label style={{ fontWeight: '600', fontSize: 13, display: 'block', marginBottom: 5, color: '#333' }}>
        {children}{required && <span style={{ color: '#e74c3c', marginLeft: 3 }}>*</span>}
    </label>
);

const FormSection = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999', marginBottom: 10 }}>
            {title}
        </div>
        {children}
    </div>
);

const EMPTY_FORM = {
    maintenanceDate: '',
    type: 'PREVENTIVE',
    lieu: 'ENTREPOT',
    nomSignataire: '',
    resultat: 'CONFORME',
    commentaire: '',
};

const CHECKLIST_DOMICILE = [
    'Vérification de l\'état et fixation des panneaux têtes et pieds',
    'Vérification Potence (fixation, sangle)',
    'Serrage des boulonneries, axes et fixation des goupilles',
    'Vérification Télécommandes',
    'Vérification Barrières',
    'Vérification Freins',
    'Vérification des câbles, prises & connecteurs',
];

const MaintenanceForm = ({ bedNumber, onSuccess }) => {
    const userName = useSelector(state => state.user.user?.name);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSignature, setShowSignature] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_FORM, maintenanceDate: toDatetimeLocal(new Date()) });
    const [checklist, setChecklist] = useState(new Set());

    const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const toggleCheck = (item) => {
        setChecklist(prev => {
            const next = new Set(prev);
            if (next.has(item)) next.delete(item);
            else next.add(item);
            return next;
        });
    };

    const buildCommentaire = () => {
        const parts = [];
        if (checklist.size > 0) {
            parts.push([...checklist].map(item => `✓ ${item}`).join('\n'));
        }
        if (form.commentaire.trim()) {
            parts.push(form.commentaire.trim());
        }
        return parts.join('\n\n') || null;
    };

    const isValid = form.maintenanceDate && form.type && form.lieu && form.resultat;

    const save = async (signatureBase64 = null) => {
        setLoading(true);
        const success = await addMaintenanceLog(bedNumber, {
            technicienNom:    userName || null,
            maintenanceDate:  form.maintenanceDate,
            type:             form.type,
            lieu:             form.lieu,
            nomSignataire:    form.nomSignataire.trim() || null,
            signaturePatient: signatureBase64,
            resultat:         form.resultat,
            commentaire:      buildCommentaire(),
            declaredBy:       userName || null,
        });
        if (success) {
            setForm({ ...EMPTY_FORM, maintenanceDate: toDatetimeLocal(new Date()) });
            setChecklist(new Set());
            setOpen(false);
            setShowSignature(false);
            onSuccess();
        }
        setLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isValid) return;
        if (form.lieu === 'DOMICILE') {
            setShowSignature(true); // intercepte → modale signature
        } else {
            save(null);
        }
    };

    if (!open) {
        return (
            <button className="btn btn-outline-warning btn-sm" onClick={() => setOpen(true)} style={{ marginBottom: 12 }}>
                + Ajouter une maintenance
            </button>
        );
    }

    return (
        <>
        {showSignature && (
            <SignatureModal
                onConfirm={(sig) => { setShowSignature(false); save(sig); }}
                onCancel={() => setShowSignature(false)}
            />
        )}
        <form onSubmit={handleSubmit} style={{
            backgroundColor: '#fffbea',
            border: '1px solid #f0c040',
            borderRadius: 8,
            padding: '18px 20px',
            marginBottom: 16,
        }}>
            <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 16, color: '#333' }}>
                Enregistrement d'une maintenance
            </div>

            <FormSection title="Date d'intervention">
                <input className="form-control form-control-sm" type="datetime-local"
                    value={form.maintenanceDate}
                    onChange={e => set('maintenanceDate', e.target.value)} required />
            </FormSection>

            <FormSection title="Nature de l'opération">
                <RadioGroup name="type" value={form.type} onChange={v => set('type', v)} options={[
                    { value: 'PREVENTIVE', label: 'Maintenance préventive', color: '#2980b9' },
                    { value: 'CORRECTIVE', label: 'Maintenance corrective', color: '#e67e22' },
                ]} />
            </FormSection>

            <FormSection title="Lieu d'intervention">
                <RadioGroup name="lieu" value={form.lieu} onChange={v => set('lieu', v)} options={[
                    { value: 'ENTREPOT',  label: 'Entrepôt',           color: '#7d3c98' },
                    { value: 'DOMICILE',  label: 'Domicile du patient', color: '#c0392b' },
                ]} />
                {form.lieu === 'DOMICILE' && (
                    <div style={{ marginTop: 10, padding: '12px 14px', backgroundColor: '#fdf3f2', borderRadius: 6, border: '1px solid #f5c6cb' }}>
                        <FieldLabel>Signataire (optionnel)</FieldLabel>
                        <input
                            className="form-control form-control-sm"
                            type="text"
                            placeholder="Laisser vide si c'est le patient du lit qui signe"
                            value={form.nomSignataire}
                            onChange={e => set('nomSignataire', e.target.value)}
                        />
                        <div style={{ fontSize: 11, color: '#c0392b', marginTop: 6 }}>
                            ✍️ Une signature sera demandée à l'étape suivante.
                        </div>
                    </div>
                )}
            </FormSection>

            {form.type === 'PREVENTIVE' && (
                <FormSection title="Checklist maintenance préventive">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {CHECKLIST_DOMICILE.map(item => (
                            <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13, userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={checklist.has(item)}
                                    onChange={() => toggleCheck(item)}
                                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#27ae60' }}
                                />
                                <span style={{ color: checklist.has(item) ? '#27ae60' : '#555', fontWeight: checklist.has(item) ? '600' : 'normal' }}>
                                    {item}
                                </span>
                            </label>
                        ))}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                        <button type="button" className="btn btn-link btn-sm"
                            style={{ padding: 0, fontSize: 11, color: '#888', textDecoration: 'underline' }}
                            onClick={() => setChecklist(new Set(CHECKLIST_DOMICILE))}>
                            Tout cocher
                        </button>
                        <span style={{ color: '#ccc', fontSize: 13 }}>|</span>
                        <button type="button" className="btn btn-link btn-sm"
                            style={{ padding: 0, fontSize: 11, color: '#888', textDecoration: 'underline' }}
                            onClick={() => setChecklist(new Set())}>
                            Tout décocher
                        </button>
                    </div>
                </FormSection>
            )}

            <FormSection title="Résultat et conformité">
                <RadioGroup name="resultat" value={form.resultat} onChange={v => set('resultat', v)} options={[
                    { value: 'CONFORME',     label: '✓ Conforme',     color: '#27ae60' },
                    { value: 'NON_CONFORME', label: '✗ Non conforme', color: '#c0392b' },
                ]} />
                <div style={{ marginTop: 10 }}>
                    <FieldLabel>Commentaire (optionnel)</FieldLabel>
                    <textarea className="form-control form-control-sm" rows={2}
                        placeholder="Observations, actions effectuées..."
                        value={form.commentaire}
                        onChange={e => set('commentaire', e.target.value)} />
                </div>
            </FormSection>

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" className="btn btn-warning btn-sm" disabled={loading || !isValid}>
                    {loading ? 'Enregistrement...' : (form.lieu === 'DOMICILE' ? 'Continuer → Signature' : 'Enregistrer la maintenance')}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setOpen(false)}>
                    Annuler
                </button>
            </div>
        </form>
        </>
    );
};

const Bed = (props) => {
    const user = useSelector(state => state.user.user);
    const [bed, setBed] = useState({});
    const [cleaningLogs, setCleaningLogs] = useState([]);
    const [maintenanceLogs, setMaintenanceLogs] = useState([]);
    const [rentalHistory, setRentalHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const bedNumber = props.match.params.number;

    const loadBed = () => {
        fetch(`${process.env.REACT_APP_API_URL}/beds/${bedNumber}`, {
            method: 'GET',
            credentials: 'include',
            headers: new Headers({ 'Content-Type': 'application/json' })
        })
        .then(async res => {
            if (res.status === 200) return res.json();
            throw await res.json();
        })
        .then(res => setBed(res))
        .catch(err => console.error(err));
    };

    const loadLogs = () => {
        fetchCleaningLogs(bedNumber).then(data => setCleaningLogs(data));
        fetchMaintenanceLogs(bedNumber).then(data => setMaintenanceLogs(data));
        fetchRentalHistory(bedNumber).then(data => setRentalHistory(data));
    };

    useEffect(() => {
        loadBed();
        loadLogs();
    }, []);

    const handleMarkAsClean = async () => {
        setLoading(true);
        const success = await markAsClean(bedNumber, user?.name);
        if (success) {
            loadBed();
            loadLogs();
        }
        setLoading(false);
    };

    const bedStatus = (() => {
        if (bed.status === 'SALE') return 'SALE';
        if (bed.status === 'EN_LOCATION' || bed.isRent) return 'EN_LOCATION';
        return 'DISPONIBLE';
    })();

    return (
        <div>
            <HeaderDashboard />
            <Container style={{ marginTop: 20, paddingBottom: 40 }}>

                {/* ── Hero card ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #2d3561 100%)',
                    borderRadius: 16,
                    padding: '24px 28px 20px',
                    marginBottom: 20,
                    color: '#fff',
                    boxShadow: '0 4px 20px rgba(26,26,46,0.18)',
                }}>
                    <a href="/dashboard" style={{
                        fontSize: 12, color: '#94a3b8', textDecoration: 'none',
                        display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 14,
                    }}>
                        ← Retour au tableau de bord
                    </a>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                        {/* Identité */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <span style={{ fontSize: 28, fontWeight: '800', letterSpacing: '-0.5px' }}>
                                    Lit #{bed.number}
                                </span>
                                <BedStatus status={bedStatus} />
                            </div>
                            {bed.client
                                ? <div style={{ fontSize: 14, color: '#94a3b8' }}>
                                    <strong style={{ color: '#e2e8f0', fontWeight: '600' }}>{bed.client}</strong>
                                    {bed.pharmacy && <span> · {bed.pharmacy}</span>}
                                    {bed.address && (
                                        <div style={{ fontSize: 12, marginTop: 4 }}>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(bed.address)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#60a5fa', textDecoration: 'none' }}
                                            >
                                                📍 {bed.address}
                                            </a>
                                        </div>
                                    )}
                                    {bed.phone && (
                                        <div style={{ fontSize: 12, marginTop: 4 }}>
                                            <a
                                                href={`tel:${bed.phone.replace(/\s/g, '')}`}
                                                style={{ color: '#60a5fa', textDecoration: 'none' }}
                                            >
                                                📞 {bed.phone}
                                            </a>
                                        </div>
                                    )}
                                  </div>
                                : <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>Aucun client affecté</div>
                            }
                        </div>
                        {/* Chips infos */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {[
                                { label: 'Modèle',   value: bed.model },
                                { label: 'Moteurs',  value: bed.motors },
                                { label: 'Année',    value: bed.year },
                                { label: 'N° série', value: bed.serialNumber },
                            ].filter(i => i.value).map(item => (
                                <div key={item.label} style={{
                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: 10,
                                    padding: '7px 14px',
                                    fontSize: 12,
                                    minWidth: 70,
                                }}>
                                    <div style={{ color: '#94a3b8', marginBottom: 2, fontSize: 11 }}>{item.label}</div>
                                    <div style={{ fontWeight: '700', fontSize: 13 }}>{item.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Alerte nettoyage ── */}
                {bedStatus === 'SALE' && (
                    <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: 12,
                        padding: '14px 20px',
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}>
                        <div>
                            <div style={{ color: '#b91c1c', fontWeight: '700', fontSize: 14, marginBottom: 2 }}>
                                ⚠ Nettoyage requis
                            </div>
                            <div style={{ color: '#dc2626', fontSize: 12 }}>
                                Ce lit doit être nettoyé et désinfecté avant d'être remis en location.
                            </div>
                        </div>
                        <button
                            className="btn btn-success btn-sm"
                            onClick={handleMarkAsClean}
                            disabled={loading}
                            style={{ borderRadius: 8, fontWeight: '600', padding: '7px 16px' }}
                        >
                            {loading ? 'Enregistrement...' : '✓ Marquer comme nettoyé'}
                        </button>
                    </div>
                )}

                <EditBedForm bed={bed} />

                <hr style={{ marginTop: 32 }} />
                <h4>Historique des locations</h4>
                <RentalHistory rentals={rentalHistory} />

                <hr style={{ marginTop: 32 }} />
                <h4>Historique des nettoyages</h4>
                <CleaningHistory logs={cleaningLogs} />

                <hr style={{ marginTop: 32 }} />
                <h4>Historique des maintenances</h4>
                <MaintenanceForm bedNumber={bedNumber} onSuccess={loadLogs} />
                <MaintenanceHistory logs={maintenanceLogs} bedClient={bed.client} />
            </Container>
        </div>
    );
};

export default Bed;
